import React, { useEffect, useState, useContext } from 'react';
import { AuthContext } from '../context/AuthContext';
import { DocumentArrowDownIcon, ArrowPathIcon } from '@heroicons/react/24/outline';
import logger from '../utils/logger';

const DOCUMENT_TYPE_LABELS = { pdf: 'PDF', word: 'Word', excel: 'Excel' };

/**
 * Reusable deliverables list: fetch unlocked templates, show list, generate & download.
 * Uses AuthContext for token. Intended for use when user is already authenticated (e.g. Profile Documents tab or /deliverables page).
 * @param {Object} props
 * @param {boolean} [props.showHeading] - If true, show page "Documents" h1 (default true). Intro copy is always the same below it or below the parent heading.
 */
function DeliverablesContent({ showHeading = true }) {
  const { token } = useContext(AuthContext);
  const [templates, setTemplates] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [generatingId, setGeneratingId] = useState(null);

  useEffect(() => {
    if (!token) return;
    fetchTemplates();
  }, [token]);

  const fetchTemplates = async () => {
    if (!token) return;
    setLoading(true);
    setError('');
    try {
      const res = await fetch(`${import.meta.env.VITE_API_URL}/api/deliverables/templates`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      if (!res.ok) {
        const data = await res.json().catch(() => ({}));
        throw new Error(data.error || 'Failed to load deliverables.');
      }
      const data = await res.json();
      setTemplates(data);
    } catch (err) {
      logger.error('Deliverables fetch error:', err);
      setError(err.message || 'Failed to load deliverables.');
      setTemplates([]);
    } finally {
      setLoading(false);
    }
  };

  const handleGenerate = async (template) => {
    if (!token) return;
    const templateId = template._id;
    setGeneratingId(templateId);
    setError('');
    try {
      const res = await fetch(`${import.meta.env.VITE_API_URL}/api/deliverables/generate`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${token}`,
        },
        body: JSON.stringify({ templateId }),
      });

      if (!res.ok) {
        const data = await res.json().catch(() => ({}));
        throw new Error(data.error || 'Generation failed.');
      }

      const blob = await res.blob();
      const disposition = res.headers.get('Content-Disposition');
      const ext = (template.documentType || 'excel').toLowerCase() === 'word' ? '.docx' : '.xlsx';
      let filename = (template.name || 'deliverable').replace(/[^\w\s-]/g, '').replace(/\s+/g, '_') + ext;
      if (disposition) {
        const match = disposition.match(/filename="?([^";\n]+)"?/);
        if (match && match[1]) filename = match[1].trim();
      }
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = filename;
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      URL.revokeObjectURL(url);
    } catch (err) {
      logger.error('Deliverables generate error:', err);
      setError(err.message || 'Failed to generate document.');
    } finally {
      setGeneratingId(null);
    }
  };

  return (
    <div className="max-w-3xl w-full min-w-0">
      {showHeading && (
        <h1 className="text-2xl sm:text-3xl font-heading font-semibold text-gray-900 dark:text-white mb-4 tracking-tight">
          Documents
        </h1>
      )}

      <div
        className="mb-5 max-w-2xl rounded-md border border-gray-200/90 dark:border-gray-600 bg-gray-50/80 dark:bg-gray-800/30 px-3 py-2.5 sm:px-3.5 sm:py-3"
        role="note"
        aria-label="About generated documents"
      >
        <p className="font-body text-sm sm:text-[0.9375rem] text-gray-700 dark:text-gray-300 leading-[1.65] mb-3">Each deliverable unlocks once you complete its required chapters and is yours to keep.
        </p>
          <ul className="font-body text-sm sm:text-[0.9375rem] text-gray-700 dark:text-gray-300 leading-[1.65] space-y-3 pl-0 sm:pl-1 list-none">
          <li className="flex gap-2">
            <span className="mt-2 h-1.5 w-1.5 shrink-0 rounded-full bg-gray-400 dark:bg-gray-500" aria-hidden />
            <span>
              <strong className="font-semibold text-gray-900 dark:text-gray-100">Unlocking.</strong>{' '}
              Generate documents from your completed course work.
            </span>
          </li>
          <li className="flex gap-2">
            <span className="mt-2 h-1.5 w-1.5 shrink-0 rounded-full bg-gray-400 dark:bg-gray-500" aria-hidden />
            <span>
              <strong className="font-semibold text-gray-900 dark:text-gray-100">Save a copy.</strong>{' '}
              Generated documents are not stored. Download to keep a copy.
            </span>
          </li>
          <li className="flex gap-2">
            <span className="mt-2 h-1.5 w-1.5 shrink-0 rounded-full bg-gray-400 dark:bg-gray-500" aria-hidden />
            <span>
              <strong className="font-semibold text-gray-900 dark:text-gray-100">Review before sharing.</strong>{' '}
              Review content, especially anything marked <i>&quot;(estimate)&quot;</i>.
            </span>
          </li>
          <li className="flex gap-2">
            <span className="mt-2 h-1.5 w-1.5 shrink-0 rounded-full bg-gray-400 dark:bg-gray-500" aria-hidden />
            <span>
              <strong className="font-semibold text-gray-900 dark:text-gray-100">Regenerate anytime.</strong>{' '}
              Re-create a fresh document as your answers evolve.
            </span>
          </li>
        </ul>
      </div>

      {error && (
        <div className="mb-4 p-3 rounded-md bg-red-50 dark:bg-red-900/20 text-red-700 dark:text-red-300 text-sm">
          {error}
        </div>
      )}

      {loading ? (
        <p className="text-gray-500 dark:text-gray-400">Loading available documents...</p>
      ) : templates.length === 0 ? (
        <div className="rounded-lg border border-gray-200 dark:border-gray-600 bg-gray-50 dark:bg-gray-800/50 p-8 text-center">
          <DocumentArrowDownIcon className="mx-auto h-12 w-12 text-gray-400 dark:text-gray-500 mb-3" />
          <p className="text-gray-600 dark:text-gray-400">
            No documents available yet. Complete the required chapters for a deliverable to unlock it.
          </p>
        </div>
      ) : (
        <ul className="space-y-4">
          {templates.map((t) => (
            <li
              key={t._id}
              className="rounded-lg border border-gray-200 dark:border-gray-600 bg-white dark:bg-gray-800 p-4 shadow-sm"
            >
              <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
                <div className="min-w-0 flex-1">
                  <h2 className="text-lg font-medium text-gray-900 dark:text-white truncate">
                    {t.name}
                  </h2>
                  {t.description && (
                    <p className="mt-1 text-sm text-gray-500 dark:text-gray-400 line-clamp-2">
                      {t.description}
                    </p>
                  )}
                  <p className="mt-1 text-xs text-gray-400 dark:text-gray-500">
                    {DOCUMENT_TYPE_LABELS[t.documentType] || t.documentType}
                  </p>
                </div>
                <button
                  type="button"
                  onClick={() => handleGenerate(t)}
                  disabled={generatingId === t._id}
                  className="inline-flex items-center justify-center gap-2 px-4 py-2 rounded-md bg-black dark:bg-white text-white dark:text-black text-sm font-medium hover:bg-gray-800 dark:hover:bg-gray-200 disabled:opacity-60 disabled:cursor-not-allowed"
                >
                  {generatingId === t._id ? (
                    <>
                      <ArrowPathIcon className="h-4 w-4 animate-spin" aria-hidden />
                      Generating…
                    </>
                  ) : (
                    <>
                      <DocumentArrowDownIcon className="h-4 w-4" aria-hidden />
                      Generate &amp; download
                    </>
                  )}
                </button>
              </div>
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}

export default DeliverablesContent;
